import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useNavigate, useSearch } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowUpRight,
  Car,
  CheckSquare,
  Download,
  RefreshCw,
  Search,
  Star,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { tendersApi } from "@/api/tenders";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  Input,
  Select,
  Spinner,
  Textarea,
} from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  calendar,
  checks,
  dateLabel,
  download,
  exportCsv,
  insurerBrief,
  isExpired,
  provinces,
  safeUrl,
  statuses,
} from "@/components/tenders/helpers";
import type { TenderBoard, TenderItem, TenderWork } from "@/types/tenders";

const key = ["tenders"];
const label = (value: string) =>
  statuses.find((s) => s.value === value)?.label ?? value;
const message = (error: unknown) =>
  error instanceof Error ? error.message : "Operacja nie powiodła się";

function Original({ item }: { item: TenderItem }) {
  const url = safeUrl(item.source.originalUrl);
  return url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm font-medium text-adara-700 underline dark:text-adara-300"
    >
      Otwórz oryginalne postępowanie <ArrowUpRight size={15} />
    </a>
  ) : (
    <span className="text-sm text-dim">
      Brak adresu oryginalnego postępowania
    </span>
  );
}

function TenderDetail({
  item,
  onClose,
  busy,
}: {
  item: TenderItem;
  onClose: () => void;
  busy: boolean;
}) {
  const cache = useQueryClient();
  const [work, setWork] = useState<TenderWork>(item.work);
  const [revision, setRevision] = useState(item.revision);
  const [baseline, setBaseline] = useState(JSON.stringify(item.work));
  const [tab, setTab] = useState("overview");
  const [evidenceCategory, setEvidenceCategory] = useState("");
  const [evidenceSearch, setEvidenceSearch] = useState("");
  const [documentBusy, setDocumentBusy] = useState<string | null>(null);
  const documentRefresh = useMutation({
    mutationFn: () => tendersApi.refreshDocuments(item.source.id),
    onSuccess: () => {
      toast.success(
        "Rozpoczęto sprawdzanie dokumentacji. Wynik pojawi się w karcie po zakończeniu.",
      );
      cache.invalidateQueries({ queryKey: key });
    },
    onError: (error) => toast.error(message(error)),
  });
  const dirty = JSON.stringify(work) !== baseline;
  useBlocker({
    shouldBlockFn: () =>
      dirty &&
      !window.confirm(
        "Masz niezapisane zmiany. Opuścić kartę bez zapisywania?",
      ),
    enableBeforeUnload: dirty,
  });
  function close() {
    onClose();
  }
  const update = (patch: Partial<TenderWork>) =>
    setWork((previous) => ({ ...previous, ...patch }));
  const save = useMutation({
    mutationFn: () => tendersApi.save(item.source.id, revision, work),
    onSuccess: (saved) => {
      setWork(saved.work);
      setRevision(saved.revision);
      setBaseline(JSON.stringify(saved.work));
      cache.setQueryData([...key, saved.source.id], saved);
      cache.setQueryData<TenderBoard>(key, (board) =>
        board
          ? {
              ...board,
              items: board.items.map((i) =>
                i.source.id === saved.source.id ? saved : i,
              ),
            }
          : board,
      );
      toast.success("Zapisano kartę przetargu");
    },
  });
  async function fetchDocument(id: string, name: string) {
    setDocumentBusy(id);
    try {
      const result = await tendersApi.document(item.source.id, id);
      download(result.blob, name);
    } catch (error) {
      toast.error(message(error));
    } finally {
      setDocumentBusy(null);
    }
  }
  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Skopiowano");
    } catch {
      toast.error("Nie udało się skopiować. Możesz pobrać tekst jako plik.");
    }
  }
  const source = item.source;
  const categories = [...new Set(source.evidence.map((e) => e.category))];
  const evidence = source.evidence.filter(
    (e) =>
      (!evidenceCategory || e.category === evidenceCategory) &&
      e.quote
        .toLocaleLowerCase("pl")
        .includes(evidenceSearch.toLocaleLowerCase("pl")),
  );
  const ready = checks.filter(([id]) => work.checklist.includes(id)).length;
  const reminderLocal = work.reminderAt
    ? new Date(
        Date.parse(work.reminderAt) -
          new Date(work.reminderAt).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";
  return (
    <Drawer
      isOpen
      onClose={close}
      title={source.title}
      subtitle={source.number}
      className="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-dim">
            {dirty ? "Niezapisane zmiany" : "Wszystkie zmiany zapisane"}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              Zamknij
            </Button>
            <Button
              disabled={!dirty}
              isLoading={save.isPending}
              onClick={() => save.mutate()}
            >
              Zapisz kartę
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Original item={item} />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => update({ favorite: !work.favorite })}
            leftIcon={
              <Star size={16} fill={work.favorite ? "currentColor" : "none"} />
            }
          >
            {work.favorite ? "Obserwowany" : "Obserwuj"}
          </Button>
        </div>
        {save.isError && (
          <Alert variant="error">
            {message(save.error)}{" "}
            <button
              type="button"
              className="underline"
              onClick={() => cache.invalidateQueries({ queryKey: key })}
            >
              Odśwież dane listy
            </button>
            . Aby wczytać nową wersję karty, zachowaj notatki i otwórz ją
            ponownie.
          </Alert>
        )}
        {isExpired(item) && (
          <Alert variant="warning">
            Termin z ogłoszenia minął. Sprawdź, czy został przedłużony, zanim
            rozpoczniesz przygotowanie oferty.
          </Alert>
        )}
        {item.revision !== revision && (
          <Alert variant="warning">
            Dane na serwerze zmieniły się od otwarcia karty. Twoje niezapisane
            notatki pozostały w formularzu.
          </Alert>
        )}
        <div
          className="flex flex-wrap gap-1 border-b border-edge pb-2"
          role="tablist"
          aria-label="Karta przetargu"
        >
          {[
            ["overview", "Przegląd"],
            ["work", "Nasza praca"],
            ["documents", "Dokumenty i wymagania"],
            ["draft", "Pakiet do TU"],
            ["history", "Historia"],
          ].map(([id, title]) => (
            <button
              type="button"
              role="tab"
              aria-selected={tab === id}
              key={id}
              onClick={() => setTab(id ?? "overview")}
              className={`rounded-md px-3 py-2 text-sm ${tab === id ? "bg-adara-100 font-semibold text-adara-800" : "text-dim hover:bg-muted"}`}
            >
              {title}
            </button>
          ))}
        </div>
        {tab === "overview" && (
          <div className="space-y-5">
            <div className="rounded-xl bg-adara-50 p-4 dark:bg-adara-900/20">
              <p className="font-semibold text-main">{source.suggestion}</p>
              <p className="mt-1 text-sm text-sub">{source.reason}</p>
              <p className="mt-2 text-xs text-dim">
                Dopasowanie do priorytetu „samochody”; to nie ocena szans na
                wygraną.
              </p>
            </div>
            <dl className="grid gap-4 text-sm sm:grid-cols-2">
              {[
                ["Organizacja z ogłoszenia", source.organization],
                [
                  "Lokalizacja organizacji",
                  `${source.city}, ${provinces[source.province] ?? source.province}`,
                ],
                ["Termin z ogłoszenia (Warszawa)", dateLabel(source.deadline)],
                ["Data publikacji", dateLabel(source.publishedAt)],
                ["Dane źródłowe sprawdzono", dateLabel(item.sourceCheckedAt)],
                ["Nasz status", label(work.status)],
              ].map(([title, value]) => (
                <div key={title}>
                  <dt className="text-dim">{title}</dt>
                  <dd className="mt-1 font-medium text-main">{value}</dd>
                </div>
              ))}
            </dl>
            <Alert variant="info">
              Organizacja i województwo mogą wskazywać pełnomocnika, a nie
              miejsce ubezpieczanego ryzyka. Potwierdź w SWZ podział na części i
              aktualne terminy; termin pytań nie jest wyliczany automatycznie.
            </Alert>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={!source.deadline}
                onClick={() => calendar(item)}
              >
                Dodaj termin do kalendarza
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  copy(
                    `${window.location.origin}/tenders?id=${encodeURIComponent(source.id)}`,
                  )
                }
              >
                Kopiuj link do karty
              </Button>
            </div>
            <details className="rounded-lg border border-edge p-3">
              <summary className="cursor-pointer text-sm font-medium text-main">
                Pełne ogłoszenie BZP i kody CPV
              </summary>
              <p className="mt-3 text-xs text-dim">{source.cpv}</p>
              <pre className="mt-3 whitespace-pre-wrap break-words font-sans text-sm text-sub">
                {source.noticeText}
              </pre>
            </details>
          </div>
        )}
        {tab === "work" && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Status pracy"
                value={work.status}
                options={statuses}
                onChange={(status) => update({ status })}
              />
              <Input
                label="Osoba prowadząca"
                maxLength={160}
                value={work.owner}
                onChange={(e) => update({ owner: e.target.value })}
                placeholder="Imię i nazwisko"
              />
              <Input
                label="Towarzystwo ubezpieczeniowe"
                maxLength={200}
                value={work.insurer}
                onChange={(e) => update({ insurer: e.target.value })}
              />
              <Select
                label="Decyzja TU"
                value={work.insurerDecision}
                onChange={(insurerDecision) => update({ insurerDecision })}
                options={[
                  { value: "pending", label: "Do ustalenia / czekamy" },
                  { value: "confirmed", label: "Potwierdzona zgoda" },
                  { value: "rejected", label: "Odmowa" },
                ]}
              />
              <Input
                label="Termin przypomnienia (czas urządzenia)"
                type="datetime-local"
                value={reminderLocal}
                onChange={(e) =>
                  update({
                    reminderAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
              <Input
                label="Ustalenia o wynagrodzeniu Adary"
                maxLength={500}
                value={work.remuneration}
                onChange={(e) => update({ remuneration: e.target.value })}
                placeholder="Do potwierdzenia z TU"
              />
            </div>
            <Checkbox
              label="Potwierdziliśmy aktualny termin w oryginalnym postępowaniu"
              checked={work.deadlineConfirmed}
              onChange={(e) => update({ deadlineConfirmed: e.target.checked })}
            />
            <Textarea
              label="Następny krok"
              maxLength={2000}
              value={work.nextStep}
              onChange={(e) => update({ nextStep: e.target.value })}
              rows={2}
            />
            <Textarea
              label="Notatki / uzasadnienie decyzji / wynik"
              maxLength={12000}
              value={work.notes}
              onChange={(e) => update({ notes: e.target.value })}
              rows={6}
            />
            <section className="space-y-3 rounded-xl border border-edge p-4">
              <h3 className="font-semibold text-main">
                Checklista — {ready}/{checks.length}
              </h3>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-adara-600"
                  style={{ width: `${(ready / checks.length) * 100}%` }}
                />
              </div>
              {checks.map(([id, title]) => (
                <Checkbox
                  key={id}
                  label={title}
                  checked={work.checklist.includes(id)}
                  onChange={(e) =>
                    update({
                      checklist: e.target.checked
                        ? [...work.checklist, id]
                        : work.checklist.filter((c) => c !== id),
                    })
                  }
                />
              ))}
            </section>
            <p className="text-xs text-dim">
              Przypomnienia są widoczne w tym module. Nie wysyłamy jeszcze
              wiadomości e-mail ani nie składamy ofert automatycznie.
            </p>
          </div>
        )}
        {tab === "documents" && (
          <div className="space-y-5">
            <Button
              variant="outline"
              isLoading={documentRefresh.isPending || busy}
              onClick={() => documentRefresh.mutate()}
              leftIcon={<RefreshCw size={15} />}
            >
              Sprawdź i pobierz dokumenty
            </Button>
            <p className="text-xs text-dim">
              Obsługiwane są publiczne bezpośrednie załączniki wybranych
              platform. Część stron wymaga dodatkowej integracji. Wynik i błędy
              będą widoczne poniżej.
            </p>
            <Alert variant="warning">{source.documentStatus}</Alert>
            {source.documents.length > 0 ? (
              <div className="space-y-2">
                {source.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-edge p-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-main">
                        {doc.name}
                      </p>
                      <p className="text-xs text-dim">
                        Pobrano: {dateLabel(doc.checkedAt)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      isLoading={documentBusy === doc.id}
                      onClick={() => fetchDocument(doc.id, doc.name)}
                      leftIcon={<Download size={14} />}
                    >
                      Pobierz
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-dim">
                Brak zapisanych załączników. Skorzystaj z oryginalnej strony
                postępowania.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Kategoria wymagań"
                value={evidenceCategory}
                onChange={setEvidenceCategory}
                options={[
                  { value: "", label: "Wszystkie kategorie" },
                  ...categories.map((c) => ({ value: c, label: c })),
                ]}
              />
              <Input
                label="Szukaj we fragmentach"
                value={evidenceSearch}
                onChange={(e) => setEvidenceSearch(e.target.value)}
              />
            </div>
            <p className="text-xs text-dim">
              Fragmenty źródłowe, do weryfikacji w pełnej dokumentacji.
              Znaleziono: {evidence.length}. Pokazujemy pierwsze 80 pasujących
              fragmentów.
            </p>
            {evidence.slice(0, 80).map((hit) => (
              <div
                key={`${hit.category}-${hit.source}-${hit.page}-${hit.line}`}
                className="rounded-lg border border-edge-soft p-3"
              >
                <p className="text-xs font-medium text-adara-700">
                  {hit.category}
                </p>
                <p className="mt-1 text-xs text-dim">
                  {source.documents.find((d) => d.id === hit.source)?.name ??
                    hit.source}{" "}
                  · strona {hit.page}, linia {hit.line}
                </p>
                <blockquote className="mt-2 whitespace-pre-line text-sm text-sub">
                  {hit.quote}
                </blockquote>
              </div>
            ))}
            {evidence.length === 0 && (
              <p className="text-sm text-dim">
                Nieustalone — brak pasujących fragmentów. Nie oznacza to braku
                wymagań.
              </p>
            )}
          </div>
        )}
        {tab === "draft" && (
          <div className="space-y-4">
            <Alert variant="info">
              Roboczy tekst do sprawdzenia. Uzupełnij dokumenty i ustalenia
              przed wysłaniem do TU. Program niczego nie wysyła.
            </Alert>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => copy(insurerBrief({ ...item, work }))}
              >
                Kopiuj pakiet do TU
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  download(
                    new Blob([insurerBrief({ ...item, work })], {
                      type: "text/plain;charset=utf-8",
                    }),
                    "pakiet-do-tu.txt",
                  )
                }
              >
                Pobierz tekst
              </Button>
            </div>
            <pre className="whitespace-pre-wrap rounded-lg bg-muted p-4 font-sans text-sm text-sub">
              {insurerBrief({ ...item, work })}
            </pre>
          </div>
        )}
        {tab === "history" && (
          <div className="space-y-3">
            {work.history.length ? (
              [...work.history].reverse().map((h) => (
                <div
                  key={`${h.at}-${h.actor}-${h.action}`}
                  className="border-l-2 border-adara-300 pl-3"
                >
                  <p className="text-sm text-main">{h.action}</p>
                  <p className="text-xs text-dim">
                    {dateLabel(h.at)} · {h.actor}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-dim">
                Brak zapisanych zmian roboczych.
              </p>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
}

export function TendersPage() {
  const navigate = useNavigate();
  const { id } = useSearch({ from: "/authenticated/tenders" });
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: key,
    queryFn: tendersApi.board,
    refetchInterval: (q) => (q.state.data?.sync.running ? 3000 : 60000),
  });
  const refresh = useMutation({
    mutationFn: tendersApi.refresh,
    onSuccess: () => {
      toast.success("Rozpoczęto odświeżanie ogłoszeń BZP");
      cache.invalidateQueries({ queryKey: key });
    },
    onError: (error) => toast.error(message(error)),
  });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [province, setProvince] = useState("");
  const [scope, setScope] = useState("vehicles");
  const [favorites, setFavorites] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);
  const [sort, setSort] = useState("priority");
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  const items = query.data?.items ?? [];
  const visible = useMemo(
    () =>
      items
        .filter(
          (i) =>
            (!search ||
              `${i.source.title} ${i.source.number} ${i.source.organization} ${i.work.owner} ${i.work.notes}`
                .toLocaleLowerCase("pl")
                .includes(search.toLocaleLowerCase("pl"))) &&
            (!status || i.work.status === status) &&
            (!province || i.source.province === province) &&
            (scope !== "vehicles" || i.source.priority <= 2) &&
            (!favorites || i.work.favorite) &&
            (!hideExpired || !isExpired(i, now)),
        )
        .sort((a, b) =>
          sort === "newest"
            ? Date.parse(b.source.publishedAt) -
              Date.parse(a.source.publishedAt)
            : sort === "deadline"
              ? (Date.parse(a.source.deadline ?? "") || Infinity) -
                (Date.parse(b.source.deadline ?? "") || Infinity)
              : a.source.priority - b.source.priority ||
                (Date.parse(a.source.deadline ?? "") || Infinity) -
                  (Date.parse(b.source.deadline ?? "") || Infinity),
        ),
    [items, search, status, province, scope, favorites, hideExpired, sort, now],
  );
  const detailQuery = useQuery({
    queryKey: [...key, id],
    queryFn: () => tendersApi.item(id ?? ""),
    enabled: !!id,
    refetchInterval: query.data?.sync.running ? 3000 : 60000,
  });
  const selected = detailQuery.data;
  const reminders = items.filter(
    (i) =>
      i.work.reminderAt &&
      Date.parse(i.work.reminderAt) <= now &&
      !["won", "lost", "rejected"].includes(i.work.status),
  );
  const urgent = items.filter(
    (i) =>
      i.source.deadline &&
      Date.parse(i.source.deadline) > now &&
      Date.parse(i.source.deadline) - now < 3 * 86400000 &&
      i.source.priority <= 2,
  );
  const open = (value?: string) =>
    navigate({ to: "/tenders", search: { id: value }, replace: true });
  return (
    <div className="space-y-5">
      <PageHeader
        title="Przetargi"
        description="Pilotaż dla administratorów. Program sugeruje kolejność przeglądu; decyzje i obsługę prowadzimy w Adarze."
        actions={
          <>
            <Button
              variant="outline"
              disabled={visible.length === 0}
              onClick={() => exportCsv(visible)}
              leftIcon={<Download size={16} />}
            >
              Eksport listy
            </Button>
            <Button
              isLoading={refresh.isPending || query.data?.sync.running}
              onClick={() => refresh.mutate()}
              leftIcon={<RefreshCw size={16} />}
            >
              {query.data?.sync.running ? "Trwa sprawdzanie" : "Odśwież BZP"}
            </Button>
          </>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="warning">Test · tylko Admin</Badge>
        <Badge variant="info">Priorytet: samochody</Badge>
        <span className="text-xs text-dim">
          {query.data?.sync.automaticEnabled
            ? "Automatyczne sprawdzanie BZP co 24 h podczas działania serwera"
            : "Automatyczne odświeżanie wyłączone"}
        </span>
      </div>
      {query.isError && (
        <Alert variant="error">
          Nie udało się pobrać przetargów. {message(query.error)}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => query.refetch()}
          >
            Spróbuj ponownie
          </button>
        </Alert>
      )}
      {query.data?.sync.error && (
        <Alert variant="warning">{query.data.sync.error}</Alert>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ["W bazie", items.length, "Wszystkie zapisane postępowania"],
          [
            "Samochody",
            items.filter((i) => i.source.priority <= 2 && !isExpired(i, now))
              .length,
            "Do sprawdzenia, w tym pakiety mieszane",
          ],
          [
            "Termin w ciągu 3 dni",
            urgent.length,
            "Termin z ogłoszenia, wymaga potwierdzenia",
          ],
          ["Przypomnienia", reminders.length, "Własne zadania do wykonania"],
        ].map(([title, count, hint]) => (
          <Card key={title}>
            <p className="text-xs text-dim">{title}</p>
            <p className="mt-1 text-2xl font-semibold text-main">{count}</p>
            <p className="mt-1 text-xs text-faint">{hint}</p>
          </Card>
        ))}
      </div>
      {reminders.length > 0 && (
        <Card>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-main">
            <CheckSquare size={16} />
            Do zrobienia
          </h2>
          <div className="space-y-2">
            {reminders.map((i) => (
              <button
                type="button"
                key={i.source.id}
                onClick={() => open(i.source.id)}
                className="block w-full rounded-md bg-muted p-2 text-left text-sm text-main"
              >
                <span className="font-medium">
                  {i.work.nextStep || "Sprawdź kartę przetargu"}
                </span>
                <span className="block text-xs text-dim">
                  {i.source.number} · {i.work.owner || "Brak prowadzącego"} ·{" "}
                  {dateLabel(i.work.reminderAt)}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}
      <Card>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="Szukaj przetargu"
            leftIcon={<Search size={16} />}
            placeholder="Nazwa, numer, organizacja, notatki…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            label="Zakres"
            value={scope}
            onChange={setScope}
            options={[
              { value: "vehicles", label: "Samochody i pakiety z pojazdami" },
              { value: "all", label: "Wszystkie ubezpieczenia" },
            ]}
          />
          <Select
            label="Status"
            value={status}
            onChange={setStatus}
            options={[{ value: "", label: "Wszystkie statusy" }, ...statuses]}
          />
          <Select
            label="Województwo organizacji"
            value={province}
            onChange={setProvince}
            options={[
              { value: "", label: "Cała Polska" },
              ...Object.entries(provinces).map(([value, title]) => ({
                value,
                label: title,
              })),
            ]}
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Checkbox
            label="Tylko obserwowane"
            checked={favorites}
            onChange={(e) => setFavorites(e.target.checked)}
          />
          <Checkbox
            label="Ukryj z minionym terminem"
            checked={hideExpired}
            onChange={(e) => setHideExpired(e.target.checked)}
          />
          <Select
            size="sm"
            className="min-w-48"
            value={sort}
            onChange={setSort}
            options={[
              { value: "priority", label: "Według sugestii programu" },
              { value: "deadline", label: "Najbliższy termin" },
              { value: "newest", label: "Najnowsze ogłoszenia" },
            ]}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSearch("");
              setStatus("");
              setProvince("");
              setScope("vehicles");
              setFavorites(false);
              setHideExpired(true);
              setSort("priority");
            }}
          >
            Wyczyść filtry
          </Button>
        </div>
      </Card>
      <div className="flex flex-wrap justify-between gap-2 text-xs text-dim">
        <span>
          Wyniki: {visible.length} / {items.length}
        </span>
        <span>
          Ostatnie udane odświeżenie BZP:{" "}
          {dateLabel(query.data?.sync.lastSuccessAt ?? null)}. Zaimportowane
          dane mają własną datę sprawdzenia w karcie.
        </span>
      </div>
      {query.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Car size={24} />}
            title="Brak pasujących przetargów"
            description="Zmień filtry, pokaż inne ubezpieczenia lub odśwież BZP."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map((i) => (
            <Card key={i.source.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <Badge
                      variant={i.source.priority <= 2 ? "info" : "default"}
                    >
                      {isExpired(i, now) ? "Termin minął" : i.source.suggestion}
                    </Badge>
                    <Badge variant="default">{label(i.work.status)}</Badge>
                    {i.work.favorite && (
                      <Star
                        size={15}
                        className="text-amber-500"
                        fill="currentColor"
                      />
                    )}
                    <span className="text-xs text-dim">{i.source.number}</span>
                  </div>
                  <button
                    type="button"
                    className="text-left text-base font-semibold text-main hover:underline"
                    onClick={() => open(i.source.id)}
                  >
                    {i.source.title}
                  </button>
                  <p className="mt-1 text-sm text-dim">
                    {i.source.organization} · {i.source.city}
                  </p>
                  <p className="mt-2 text-sm text-sub">{i.source.reason}</p>
                </div>
                <Button variant="outline" onClick={() => open(i.source.id)}>
                  Otwórz kartę
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-edge-soft pt-3">
                <div className="text-sm text-sub">
                  <span className="font-medium">
                    Termin: {dateLabel(i.source.deadline)}
                  </span>
                  {!i.work.deadlineConfirmed && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
                      <AlertTriangle size={12} />
                      do potwierdzenia
                    </span>
                  )}
                  <span className="mt-1 block text-xs text-dim">
                    Prowadzący: {i.work.owner || "nieprzypisany"} · Dokumenty:{" "}
                    {i.source.documents.length} · Checklista:{" "}
                    {i.work.checklist.length}/{checks.length}
                  </span>
                </div>
                <Original item={i} />
              </div>
            </Card>
          ))}
        </div>
      )}
      <p className="text-xs text-dim">
        Źródło: BZP. Wyszukiwanie po frazie w przedmiocie nie obejmuje całego
        rynku, TED ani wszystkich korekt i unieważnień. Sprawdź oryginalne
        postępowanie przed działaniem.
      </p>
      {selected && (
        <TenderDetail
          key={selected.source.id}
          item={selected}
          busy={query.data?.sync.running ?? false}
          onClose={() => open()}
        />
      )}
      {id && detailQuery.isPending && (
        <div className="flex justify-center p-4">
          <Spinner />
        </div>
      )}
      {id && detailQuery.isError && (
        <Alert variant="warning">
          Nie znaleziono wskazanej karty.{" "}
          <button type="button" className="underline" onClick={() => open()}>
            Wróć do listy
          </button>
        </Alert>
      )}
    </div>
  );
}
